import { IS_CLINIC_APP } from "@/config/constants";

// Storage key constant
export const PREFERRED_ORGANIZATION_ID_KEY = "preferredOrganizationId";

type TimezoneDefaults = { currency: string; region: string };

const TIMEZONE_DEFAULTS: Record<string, TimezoneDefaults> = {
  "Asia/Kathmandu": { currency: "NPR", region: "NP" },
  "Asia/Dubai": { currency: "AED", region: "AE" },
};

// ClinicPilot bills every clinic in AED, wherever it is: the organisation
// starts on AED so its settings and its bill never disagree.
const CLINIC_APP_CURRENCY = "AED";

export const getDefaultCurrencyFromTimezone = (
  timezone: string,
  isClinicApp: boolean = IS_CLINIC_APP,
): string => {
  if (isClinicApp) return CLINIC_APP_CURRENCY;

  return TIMEZONE_DEFAULTS[timezone]?.currency ?? "USD";
};

/**
 * Timezone first because it tracks where the business actually is; the browser
 * locale is a UI-language preference and reads "US" for most of the world.
 */
export const getDefaultRegionFromTimezone = (timezone: string): string => {
  const mapped = TIMEZONE_DEFAULTS[timezone]?.region;

  if (mapped) return mapped;

  try {
    return new Intl.Locale(navigator.language).region ?? "US";
  } catch {
    return "US";
  }
};

// Get saved organization preference
export const getPreferredOrganizationId = (): string | null => {
  try {
    return localStorage.getItem(PREFERRED_ORGANIZATION_ID_KEY);
  } catch (error) {
    console.error("Error reading organization preference:", error);

    return null;
  }
};

// Save organization preference
export const setPreferredOrganizationId = (organizationId: string): void => {
  try {
    localStorage.setItem(PREFERRED_ORGANIZATION_ID_KEY, organizationId);
  } catch (error) {
    console.error("Error saving organization preference:", error);
  }
};

// Clear organization preference
export const clearPreferredOrganizationId = (): void => {
  try {
    localStorage.removeItem(PREFERRED_ORGANIZATION_ID_KEY);
  } catch (error) {
    console.error("Error clearing organization preference:", error);
  }
};

// Get organization to activate on login (checks localStorage, validates membership)
export const getOrganizationToActivate = (
  organizationMemberships: Array<{ organization: { id: string } }>,
): string | null => {
  if (!organizationMemberships || organizationMemberships.length === 0) {
    return null;
  }

  const preferredId = getPreferredOrganizationId();

  // Validate saved preference against current memberships
  if (preferredId) {
    const hasAccess = organizationMemberships.some(
      (membership) => membership.organization.id === preferredId,
    );

    if (hasAccess) {
      return preferredId;
    }
    // Clear invalid preference (user was removed from org)
    clearPreferredOrganizationId();
  }

  // Fallback to first organization
  return organizationMemberships[0].organization.id;
};
